import dayjs from 'dayjs';

import React, { memo } from 'react';

import { ChevronRight, Delete, ExpandMore, FormatListNumbered, SubdirectoryArrowLeft, SubdirectoryArrowRight, Timer } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, ButtonBase, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { formatDuration, formatTimestamp, sortByOrder } from '@@common';

import { StepType } from '../../../api/api';
import CopyToClipboardButton from '../../../components/Clipboard/CopyToClipboardButton';
import ActivityNameDisplay from './ActivityNameDisplay';
import JsonDataDisplay from '../../../common/components/JsonDataDisplay';

const FLOW_INSTANCE_STEP_DATA_SNAPSHOT_SETTINGS_KEY = 'flowInstanceExecutionRun.dataSnapshot';
const FLOW_INSTANCE_STEP_DATA_SNAPSHOT_EXPANDED_STORAGE_KEY = 'flowInstanceExecutionRun.dataSnapshot.expanded';
const VARIABLE_SNAPSHOT_METADATA_KEY = '$variableSnapshot';
const VARIABLE_SNAPSHOT_MODE_KEY = 'mode';
const VARIABLE_SNAPSHOT_DIFF_MODE = 'diff';

interface ExecutionStep {
	id: string;
	type: StepType;
	executionId: string;
	activityId: string;
	parentActivityId?: string | undefined;
	name?: string | undefined;
	timestamp: string;
	data?: { [key: string]: any } | undefined;
	order: number;
}

interface FlowInstanceExecutionRunProps {
	groupIndex: number;
	executionId: string;
	runId: string;
	steps: ExecutionStep[];
	startIndex: number;
	selectedStepIndex: number | null;
	onStepClick: (stepIndex: number, activityId?: string, stepId?: string) => void;
	getActivityType: (activityId: string | undefined) => string | undefined;
	getActivityData: (activityId: string | undefined) => any;
	onDeleteRun?: (runId: string) => void;
	isLastRun?: boolean;
}

function FlowInstanceExecutionRun({
	groupIndex,
	executionId,
	runId: _runId,
	steps,
	startIndex,
	selectedStepIndex,
	onStepClick,
	getActivityType,
	getActivityData,
	onDeleteRun,
	isLastRun = false,
}: FlowInstanceExecutionRunProps) {
	const [expanded, setExpanded] = React.useState(isLastRun);
	React.useEffect(() => {
		if (selectedStepIndex !== null) setExpanded(true);
	}, [selectedStepIndex]);
	const [collapsedParents, setCollapsedParents] = React.useState<Set<string>>(() => new Set());
	const revealedStepId = React.useRef<string>();
	const toggleNestedSteps = React.useCallback((stepId: string) => {
		setCollapsedParents((previous) => {
			const next = new Set(previous);
			if (next.has(stepId)) next.delete(stepId);
			else next.add(stepId);
			return next;
		});
	}, []);

	// Sort steps by order property
	const sortedSteps = React.useMemo(() => {
		return [...steps].sort(sortByOrder);
	}, [steps]);

	// Precompute: Build a map of Leave step indices to their matching Enter step indices
	const leaveToEnterMap = React.useMemo(() => {
		if (!expanded) {
			return new Map<number, number>();
		}

		const map = new Map<number, number>();
		sortedSteps.forEach((step, idx) => {
			if (step.type === StepType.OnLeave) {
				for (let i = idx - 1; i >= 0; i--) {
					const previousStep = sortedSteps[i];
					if (
						previousStep.activityId === step.activityId &&
						previousStep.parentActivityId === step.parentActivityId &&
						previousStep.type === StepType.OnEnter
					) {
						map.set(idx, i);
						break;
					}
				}
			}
		});
		return map;
	}, [expanded, sortedSteps]);

	// Precompute: Calculate nesting level for each step based on execution trace
	const nestingLevels = React.useMemo(() => {
		if (!expanded) {
			return new Map<number, number>();
		}

		const levels = new Map<number, number>(); // stepIndex -> nesting level
		const activeStack: string[] = []; // Stack of currently active activityIds

		sortedSteps.forEach((step, idx) => {
			if (step.type === StepType.OnEnter) {
				// Nesting level = current stack depth
				levels.set(idx, activeStack.length);
				// Push this activity onto the stack
				activeStack.push(step.activityId);
			} else if (step.type === StepType.OnLeave) {
				// Pop the most recent matching activity from the stack
				const lastIndex = activeStack.lastIndexOf(step.activityId);
				if (lastIndex !== -1) {
					activeStack.splice(lastIndex, 1);
				}
				// Nesting level = current stack depth (after pop)
				levels.set(idx, activeStack.length);
			}
		});

		return levels;
	}, [expanded, sortedSteps]);

	// Descendants are the contiguous deeper rows, ending before the parent's Leave row.
	const nestedGroupEnds = React.useMemo(() => {
		const ends = new Map<number, number>();
		const parents: number[] = [];
		for (let index = 0; index < sortedSteps.length; index++) {
			const level = nestingLevels.get(index) ?? 0;
			while (parents.length && (nestingLevels.get(parents[parents.length - 1]) ?? 0) >= level) {
				ends.set(parents.pop()!, index);
			}
			if (sortedSteps[index].type === StepType.OnEnter && (nestingLevels.get(index + 1) ?? 0) > level) {
				parents.push(index);
			}
		}
		for (const parent of parents) ends.set(parent, sortedSteps.length);
		return ends;
	}, [nestingLevels, sortedSteps]);
	React.useEffect(() => {
		if (selectedStepIndex === null) { revealedStepId.current = undefined; return; }
		if (!expanded) return;
		const index = selectedStepIndex - startIndex;
		const stepId = sortedSteps[index]?.id;
		if (!stepId || revealedStepId.current === stepId) return;
		revealedStepId.current = stepId;
		setCollapsedParents((previous) => {
			const parents = Array.from(nestedGroupEnds).filter(([parent, end]) => parent < index && index < end && previous.has(sortedSteps[parent].id));
			if (!parents.length) return previous;
			const next = new Set(previous);
			for (const [parent] of parents) next.delete(sortedSteps[parent].id);
			return next;
		});
	}, [expanded, selectedStepIndex, startIndex, nestedGroupEnds, sortedSteps]);

	const visibleStepIndices = React.useMemo(() => {
		const indices: number[] = [];
		for (let index = 0; index < sortedSteps.length; index++) {
			indices.push(index);
			const end = nestedGroupEnds.get(index);
			if (end !== undefined && collapsedParents.has(sortedSteps[index].id)) index = end - 1;
		}
		return indices;
	}, [collapsedParents, nestedGroupEnds, sortedSteps]);

	// Calculate total run time from first to last activity
	const totalRunTime = React.useMemo(() => {
		if (sortedSteps.length < 2) return 0;
		const firstTimestamp = sortedSteps[0].timestamp;
		const lastTimestamp = sortedSteps[sortedSteps.length - 1].timestamp;
		return dayjs(lastTimestamp).diff(dayjs(firstTimestamp), 'millisecond');
	}, [sortedSteps]);

	const stepDisplayNumbers = React.useMemo(() => {
		if (!expanded) {
			return new Map<number, number>();
		}

		const numbers = new Map<number, number>();
		let nextStepNumber = 1;

		sortedSteps.forEach((step, stepIndex) => {
			if (step.type === StepType.OnLeave) {
				const enterIndex = leaveToEnterMap.get(stepIndex);
				const enterStepNumber = enterIndex !== undefined ? numbers.get(enterIndex) : undefined;

				if (enterStepNumber !== undefined) {
					numbers.set(stepIndex, enterStepNumber);
					return;
				}
			}

			numbers.set(stepIndex, nextStepNumber);
			nextStepNumber += 1;
		});

		return numbers;
	}, [expanded, leaveToEnterMap, sortedSteps]);

	const renderStep = (step: (typeof sortedSteps)[number], stepIndexInGroup: number) => {
		const absoluteIndex = startIndex + stepIndexInGroup;
		const stepDisplayNumber = stepDisplayNumbers.get(stepIndexInGroup) ?? absoluteIndex + 1;

		// Calculate duration for Leave events using precomputed map
		let enterTimestamp: string | undefined;
		if (step.type === StepType.OnLeave) {
			const enterIndex = leaveToEnterMap.get(stepIndexInGroup);
			if (enterIndex !== undefined) {
				const enterStep = sortedSteps[enterIndex];
				enterTimestamp = enterStep.timestamp;
			}
		}

		// Get precomputed nesting level and convert to indent spacing
		const nestingLevel = nestingLevels.get(stepIndexInGroup) || 0;
		const previousNestingLevel = stepIndexInGroup > 0 ? nestingLevels.get(stepIndexInGroup - 1) || 0 : 0;
		const nextNestingLevel = stepIndexInGroup < sortedSteps.length - 1 ? nestingLevels.get(stepIndexInGroup + 1) || 0 : 0;
		const previousStep = stepIndexInGroup > 0 ? sortedSteps[stepIndexInGroup - 1] : undefined;
		const startsNestedGroup = stepIndexInGroup > 0 && nestingLevel > previousNestingLevel;
		const endsNestedGroup = stepIndexInGroup < sortedSteps.length - 1 && nextNestingLevel < nestingLevel;
		const startsSiblingPair = previousStep?.type === StepType.OnLeave && step.type === StepType.OnEnter && nestingLevel === previousNestingLevel;
		const indentPx = nestingLevel * 17; // 17px per nesting level

		return (
			<ExecutionStepRow
				key={step.id}
				step={step}
				stepTimestamp={step.timestamp}
				stepDisplayNumber={stepDisplayNumber}
				selected={selectedStepIndex === absoluteIndex}
				enterTimestamp={enterTimestamp}
				indentPx={indentPx}
				marginTop={startsNestedGroup || startsSiblingPair ? 0.75 : 0}
				marginBottom={endsNestedGroup ? 0.75 : 0}
				stepIndex={absoluteIndex}
				getActivityType={getActivityType}
				getActivityData={getActivityData}
				onStepClick={onStepClick}
				nestedCount={(nestedGroupEnds.get(stepIndexInGroup) ?? stepIndexInGroup + 1) - stepIndexInGroup - 1}
				nestedCollapsed={collapsedParents.has(step.id)}
				onToggleNested={toggleNestedSteps}
			/>
		);
	};

	return (
		<Accordion
			disableGutters
			elevation={0}
			expanded={expanded}
			onChange={(_, isExpanded) => setExpanded(isExpanded)}
			sx={{ '&:before': { display: 'none' }, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
		>
			<AccordionSummary
				expandIcon={<ExpandMore />}
				sx={{
					minHeight: '48px',
					'&.Mui-expanded': { minHeight: '48px' },
					px: 1.5,
					py: 1,
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
					<Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
						Execution Run {groupIndex + 1}
					</Typography>
					<Stack direction="row" spacing={0.5} alignItems="center">
						{totalRunTime > 0 && (
							<Stack direction="row" spacing={0.5} alignItems="center" sx={{ pr: 1 }}>
								<Timer sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
								<Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
									{formatDuration(totalRunTime)}
								</Typography>
							</Stack>
						)}
						<Stack direction="row" spacing={0.5} alignItems="center" sx={{ pr: 1 }}>
							<FormatListNumbered sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
							<Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
								{sortedSteps.length} {sortedSteps.length === 1 ? 'step' : 'steps'}
							</Typography>
						</Stack>
						{onDeleteRun && (
							<Tooltip title="Delete run">
								<IconButton
									component="div"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										onDeleteRun(executionId);
									}}
									sx={{ color: 'error.main', ml: 0.5 }}
								>
									<Delete fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				</Stack>
			</AccordionSummary>
			{expanded && (
				<AccordionDetails sx={{ p: 1.5, pt: 0 }}>
					<Stack spacing={0} alignItems="stretch">
						{visibleStepIndices.map((index) => renderStep(sortedSteps[index], index))}
					</Stack>
				</AccordionDetails>
			)}
		</Accordion>
	);
}

interface ExecutionStepRowProps {
	nestedCount: number;
	nestedCollapsed: boolean;
	onToggleNested: (stepId: string) => void;
	step: ExecutionStep;
	stepTimestamp: string;
	stepDisplayNumber: number;
	selected: boolean;
	enterTimestamp?: string;
	indentPx: number;
	marginTop: number;
	marginBottom: number;
	stepIndex: number;
	getActivityType: (activityId: string | undefined) => string | undefined;
	getActivityData: (activityId: string | undefined) => any;
	onStepClick: (stepIndex: number, activityId?: string, stepId?: string) => void;
}

const ExecutionStepRow = memo(function ExecutionStepRow({
	nestedCount,
	nestedCollapsed,
	onToggleNested,
	step,
	stepTimestamp,
	stepDisplayNumber,
	selected,
	enterTimestamp,
	indentPx,
	marginTop,
	marginBottom,
	stepIndex,
	getActivityType,
	getActivityData,
	onStepClick,
}: ExecutionStepRowProps) {
	const isEntering = step.type === StepType.OnEnter;
	const isExecution = step.type === StepType.Execution;
	const phase = step.data?.phase ?? step.name ?? 'Event';
	const stepLabel = isExecution ? phase : isEntering ? 'Enter' : step.type === StepType.OnPause ? 'Pause' : 'Leave';
	const handleClick = React.useCallback(() => {
		onStepClick(stepIndex, step.activityId, step.id);
	}, [onStepClick, step.activityId, step.id, stepIndex]);

	const formattedTimestamp = React.useMemo(() => formatTimestamp(stepTimestamp, true), [stepTimestamp]);

	const formattedDuration = React.useMemo(() => {
		if (!enterTimestamp) {
			return undefined;
		}

		const durationMs = dayjs(stepTimestamp).diff(dayjs(enterTimestamp), 'millisecond');
		return durationMs > 0 ? formatDuration(durationMs) : undefined;
	}, [enterTimestamp, stepTimestamp]);

	return (
		<Box
			sx={{
				mt: marginTop,
				mb: marginBottom,
				paddingLeft: `${indentPx}px`,
				position: 'relative',
			}}
		>
			{Array.from({ length: indentPx / 17 }, (_, level) => (
				<Box key={level} aria-hidden="true" sx={{ position: 'absolute', left: level * 17 + 1, top: -6, bottom: -6,
					width: 2, bgcolor: 'divider', pointerEvents: 'none' }} />
			))}
			<Box
				sx={{
					border: '2px solid',
					borderColor: selected ? 'primary.main' : 'divider',
					borderLeft: '4px solid',
					borderLeftColor: isEntering ? 'primary.light' : 'success.light',
					borderTopColor: selected ? 'primary.main' : isEntering ? 'primary.light' : 'divider',
					borderBottomColor: selected ? 'primary.main' : isEntering ? 'divider' : 'success.light',
					borderRadius: isEntering ? '8px 8px 2px 2px' : '2px 2px 8px 8px',
					overflow: 'hidden',
					cursor: 'pointer',
					transition: 'all 0.2s',
					'&:hover': {
						borderColor: selected ? 'primary.main' : 'primary.light',
						bgcolor: 'action.hover',
					},
				}}
				onClick={handleClick}
			>
				<Box
					sx={{
						px: 1.5,
						py: isEntering ? 1.25 : 0.85,
						backgroundImage: (theme) =>
							step.type === StepType.OnEnter
								? `linear-gradient(${theme.palette.primary.main}0A, ${theme.palette.primary.main}0A)`
								: `linear-gradient(${theme.palette.success.main}0A, ${theme.palette.success.main}0A)`,
					}}
				>
					<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
							{nestedCount > 0 && (
								<Tooltip title={`${nestedCollapsed ? 'Expand' : 'Collapse'} ${nestedCount} nested steps`}>
									<IconButton size="small" aria-label={`${nestedCollapsed ? 'Expand' : 'Collapse'} nested steps for step ${stepDisplayNumber}`}
										aria-expanded={!nestedCollapsed}
										onClick={(event) => { event.stopPropagation(); onToggleNested(step.id); }} sx={{ p: 0.25 }}>
										{nestedCollapsed ? <ChevronRight fontSize="small" /> : <ExpandMore fontSize="small" />}
									</IconButton>
								</Tooltip>
							)}
							<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
								Step {String(stepDisplayNumber).padStart(3, '0')}
							</Typography>
							<Chip
								label={stepLabel}
								icon={isExecution ? <Timer titleAccess="Flow execution event" /> : isEntering ? <SubdirectoryArrowRight titleAccess="Entering activity" /> : <SubdirectoryArrowLeft titleAccess="Leaving activity" />}
								size="small"
								color={step.type === StepType.OnEnter ? 'primary' : step.type === StepType.OnLeave ? 'success' : 'warning'}
								variant={isEntering ? 'filled' : 'outlined'}
								sx={{ fontSize: '0.7rem', height: '22px', minWidth: '75px', '& .MuiChip-icon': { fontSize: 16 } }}
							/>
						</Stack>
						<Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.65rem', flexShrink: 0, textAlign: 'right' }}>
							{formattedTimestamp}
						</Typography>
					</Stack>
					<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
						<Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
							{isExecution ? (
								<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
									Flow execution {String(phase).toLowerCase()}
									{step.data?.returnReason ? ` — ${step.data.returnReason}` : ''}
								</Typography>
							) : <ActivityNameDisplay
								activityId={step.activityId}
								getActivityType={getActivityType}
								getActivityData={getActivityData}
								showActivityId={false}
								sx={{ fontSize: '0.75rem', color: 'text.primary' }}
							/>}
							{step.name && (
								<Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
									({step.name})
								</Typography>
							)}
						</Box>
						{formattedDuration && (
							<Chip
								label={formattedDuration}
								size="small"
								variant="filled"
								sx={{
									fontSize: '0.65rem',
									height: '20px',
									bgcolor: 'info.light',
									color: 'info.contrastText',
									fontWeight: 'bold',
								}}
							/>
						)}
						<Box onClick={(event) => event.stopPropagation()}>
							<CopyToClipboardButton value={step.activityId} title="Copy Activity ID" iconSize={14} edge={false} sx={{ p: 0.25 }} />
						</Box>
					</Stack>
				</Box>
				{step.data && Object.keys(step.data).length > 0 && (
					<JsonDataDisplay
						title={
							step.data[VARIABLE_SNAPSHOT_METADATA_KEY]?.[VARIABLE_SNAPSHOT_MODE_KEY] === VARIABLE_SNAPSHOT_DIFF_MODE
								? 'Data Changes'
								: 'Data Snapshot'
						}
						data={step.data}
						asAccordion={true}
						showDivider={true}
						settingsKey={FLOW_INSTANCE_STEP_DATA_SNAPSHOT_SETTINGS_KEY}
						accordionExpandedStorageKey={FLOW_INSTANCE_STEP_DATA_SNAPSHOT_EXPANDED_STORAGE_KEY}
					/>
				)}
			</Box>
			{nestedCount > 0 && nestedCollapsed && (
				<ButtonBase
					aria-label={`Show ${nestedCount} collapsed nested steps for step ${stepDisplayNumber}`}
					onClick={() => onToggleNested(step.id)}
					sx={{ display: 'flex', width: 'calc(100% - 17px)', ml: '17px', my: 0.5, px: 1, py: 0.4, gap: 0.75,
						border: '1px dashed', borderColor: 'divider', borderRadius: 1, color: 'text.secondary', bgcolor: 'action.hover',
						'&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}>
					<Typography component="span" sx={{ lineHeight: 1, fontWeight: 700 }}>…</Typography>
					<Typography variant="caption">{nestedCount} nested steps hidden</Typography>
				</ButtonBase>
			)}
		</Box>
	);
}, areEqualExecutionStepRows);

function areEqualExecutionStepRows(previous: ExecutionStepRowProps, next: ExecutionStepRowProps) {
	return (
		previous.nestedCount === next.nestedCount &&
		previous.nestedCollapsed === next.nestedCollapsed &&
		previous.onToggleNested === next.onToggleNested &&
		previous.step === next.step &&
		previous.stepTimestamp === next.stepTimestamp &&
		previous.stepDisplayNumber === next.stepDisplayNumber &&
		previous.selected === next.selected &&
		previous.enterTimestamp === next.enterTimestamp &&
		previous.indentPx === next.indentPx &&
		previous.marginTop === next.marginTop &&
		previous.marginBottom === next.marginBottom &&
		previous.stepIndex === next.stepIndex &&
		previous.getActivityType === next.getActivityType &&
		previous.getActivityData === next.getActivityData &&
		previous.onStepClick === next.onStepClick
	);
}

export default memo(FlowInstanceExecutionRun);
